import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import type { Finding } from '../../lib/types';
import { GAP, GUTTER, calloutText, layoutCallouts, severityColor } from './calloutLayout';

// The worker MUST be the same pdf.js version react-pdf bundles (6.x). The root `pdfjs-dist` (4.x) is the server's
// copy (positions/pdfjs.ts), so the SPA uses the `pdfjs-dist-web` alias pinned to react-pdf's exact version
// (tests/unit/pdfjs-version.test.ts guards against drift).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist-web/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// ---- component --------------------------------------------------------------------------------------------------
/**
 * PDF (react-pdf) or image with finding boxes (tech design 14.3). With `callouts` on, every open finding gets a
 * numbered instruction callout in the page margin connected to its box by a leader line, and required elements that
 * are missing from the artwork are listed as dashed callouts under the page - so the designer sees exactly what to fix.
 */
export function DocViewer({
  fileUrl,
  mime,
  items,
  numbers,
  selected,
  onSelect,
  zoom,
  callouts,
  missingLabel,
}: {
  fileUrl: string;
  mime: string;
  items: Finding[];
  numbers: Record<string, number>;
  selected: string | null;
  onSelect: (id: string) => void;
  zoom: number;
  callouts: boolean;
  missingLabel: string;
}) {
  const [numPages, setNumPages] = useState(1);
  const isPdf = mime === 'application/pdf';
  const pages = isPdf ? Array.from({ length: numPages }, (_, i) => i + 1) : [1];
  useEffect(() => {
    if (!selected) return;
    document
      .getElementById(`callout-${selected}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    if (!document.getElementById(`callout-${selected}`))
      document.getElementById(`pin-${selected}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selected]);
  const visible = items.filter((f) => f.disposition !== 'overridden' && f.severity !== 'ok');
  const pinned = visible.filter((f) => f.location?.bbox);
  const missing = visible.filter((f) => !f.location?.bbox);
  const pageOf = (f: Finding) => Math.min(Math.max(f.location?.page ?? 1, 1), pages.length);

  const render = (p: number, content: ReactNode) => (
    <PageFrame
      key={p}
      page={p}
      pinned={pinned.filter((f) => f.location!.page === p)}
      missing={callouts ? missing.filter((f) => pageOf(f) === p) : []}
      numbers={numbers}
      selected={selected}
      onSelect={onSelect}
      callouts={callouts}
      missingLabel={missingLabel}
    >
      {content}
    </PageFrame>
  );
  return (
    <div className="space-y-4">
      {isPdf ? (
        <Document
          file={fileUrl}
          onLoadSuccess={(d) => setNumPages(d.numPages)}
          loading={<p className="text-sm text-slate-500">Loading PDF…</p>}
          error={<p className="text-sm text-red-600">Could not render the PDF.</p>}
        >
          {pages.map((p) =>
            render(
              p,
              <Page pageNumber={p} scale={zoom} renderAnnotationLayer={false} renderTextLayer={false} />,
            ),
          )}
        </Document>
      ) : (
        render(
          1,
          <img
            src={fileUrl}
            alt=""
            style={{ width: `${zoom * 600}px`, maxWidth: 'none', display: 'block' }}
          />,
        )
      )}
    </div>
  );
}

function PageFrame({
  page,
  pinned,
  missing,
  numbers,
  selected,
  onSelect,
  callouts,
  missingLabel,
  children,
}: {
  page: number;
  pinned: Finding[];
  missing: Finding[];
  numbers: Record<string, number>;
  selected: string | null;
  onSelect: (id: string) => void;
  callouts: boolean;
  missingLabel: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const g = callouts ? GUTTER : 0;
  const { placed, height } =
    callouts && size.w ? layoutCallouts(pinned, numbers, size.w, size.h) : { placed: [], height: size.h };
  const dim = (id: string) => (selected && selected !== id ? 0.45 : 1);

  return (
    <div className="mx-auto w-fit">
      <div
        className="relative"
        style={{ width: size.w + 2 * g, height: Math.max(height, size.h) || undefined }}
      >
        {/* the page itself */}
        <div ref={ref} className="absolute top-0 bg-white shadow" style={{ left: g, lineHeight: 0 }}>
          {children}
          <div className="absolute right-1 top-1 rounded bg-white/80 px-1 text-[10px] leading-4 text-slate-500">
            p.{page}
          </div>
        </div>
        {/* leader lines under the boxes/callouts */}
        {callouts && size.w > 0 && (
          <svg
            className="pointer-events-none absolute left-0 top-0"
            width={size.w + 2 * g}
            height={Math.max(height, size.h)}
          >
            {placed.map((c) => {
              const col = severityColor(c.f.severity);
              const cx = c.side === 'left' ? g - GAP : g + size.w + GAP;
              const cy = c.top + Math.min(c.height / 2, 22);
              const mid = c.side === 'left' ? cx + (c.ax - cx) * 0.35 : cx - (cx - c.ax) * 0.35;
              return (
                <g key={c.f.id} opacity={dim(c.f.id)}>
                  <path
                    d={`M ${cx} ${cy} L ${mid} ${cy} L ${c.ax} ${c.ay}`}
                    fill="none"
                    stroke={col}
                    strokeWidth={selected === c.f.id ? 2.5 : 1.5}
                  />
                  <circle cx={c.ax} cy={c.ay} r={3.5} fill={col} />
                </g>
              );
            })}
          </svg>
        )}
        {/* finding boxes */}
        <div
          className="pointer-events-none absolute top-0"
          style={{ left: g, width: size.w, height: size.h }}
        >
          {pinned.map((f) => {
            const [x, y, w, hgt] = f.location!.bbox!;
            const c = severityColor(f.severity);
            const approx = f.location!.matchScore < 0.9;
            return (
              <button
                key={f.id}
                id={`pin-${f.id}`}
                title={`${f.subject}: ${f.finding}`}
                onClick={() => onSelect(f.id)}
                className="pointer-events-auto absolute rounded-sm"
                style={{
                  left: `${x * 100}%`,
                  top: `${y * 100}%`,
                  width: `${Math.max(w, 0.01) * 100}%`,
                  height: `${Math.max(hgt, 0.01) * 100}%`,
                  border: `2px ${approx ? 'dashed' : 'solid'} ${c}`,
                  background: selected === f.id ? `${c}33` : `${c}14`,
                  boxShadow: selected === f.id ? `0 0 0 3px ${c}66` : undefined,
                  opacity: dim(f.id),
                }}
              >
                <span
                  className="absolute -left-1 -top-5 rounded px-1 text-[10px] font-bold leading-4 text-white"
                  style={{ background: c }}
                >
                  {numbers[f.id] ?? '·'}
                </span>
              </button>
            );
          })}
        </div>
        {/* instruction callouts in the margins */}
        {placed.map((c) => (
          <Callout
            key={c.f.id}
            f={c.f}
            n={c.n}
            selected={selected === c.f.id}
            opacity={dim(c.f.id)}
            onSelect={onSelect}
            style={{
              left: c.side === 'left' ? GAP : g + size.w + GAP,
              top: c.top,
              width: GUTTER - 2 * GAP,
              height: c.height,
            }}
          />
        ))}
      </div>
      {/* required elements that are not on the artwork: nothing to point at -> dashed callouts under the page */}
      {missing.length > 0 && (
        <div className="mt-3 rounded-md border border-dashed border-slate-400 bg-white/70 p-2">
          <p className="mb-2 text-xs font-semibold text-slate-600">{missingLabel}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {missing.map((f) => (
              <Callout
                key={f.id}
                f={f}
                n={numbers[f.id] ?? 0}
                selected={selected === f.id}
                opacity={dim(f.id)}
                onSelect={onSelect}
                dashed
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Callout({
  f,
  n,
  selected,
  opacity,
  onSelect,
  style,
  dashed,
}: {
  f: Finding;
  n: number;
  selected: boolean;
  opacity: number;
  onSelect: (id: string) => void;
  style?: CSSProperties;
  dashed?: boolean;
}) {
  const col = severityColor(f.severity);
  const c = calloutText(f);
  return (
    <button
      id={`callout-${f.id}`}
      type="button"
      onClick={() => onSelect(f.id)}
      title={`${f.subject}: ${f.fix.what || f.finding}`}
      className={`${style ? 'absolute' : 'relative w-full'} overflow-hidden rounded-lg bg-white px-2 py-1.5 text-left text-[11px] leading-[14px] shadow-sm transition hover:shadow-md`}
      style={{
        ...style,
        opacity,
        border: `${selected ? 2 : 1.5}px ${dashed ? 'dashed' : 'solid'} ${col}`,
        boxShadow: selected ? `0 0 0 3px ${col}33` : undefined,
      }}
    >
      <span className="flex items-start gap-1">
        <span
          className="mt-px inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
          style={{ background: col }}
        >
          {n || '·'}
        </span>
        <span className="line-clamp-2 font-semibold leading-[15px] text-slate-900">{c.title}</span>
      </span>
      <span className="mt-0.5 line-clamp-4 block text-slate-700">{c.body}</span>
      {c.suggestion && (
        <span className="mt-1 line-clamp-2 block rounded bg-emerald-50 px-1 font-mono text-[10px] text-emerald-900">
          → {c.suggestion}
        </span>
      )}
    </button>
  );
}
